import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats } from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../context/AuthContext';

const StatCard = ({ label, value, color, icon }) => (
  <div style={{
    background: '#fff', borderRadius: 12, padding: '24px 28px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 20,
  }}>
    <div style={{
      width: 52, height: 52, borderRadius: 12,
      background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 24,
    }}>{icon}</div>
    <div>
      <p style={{ fontSize: 13, color: '#64748b', margin: 0, marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 32, fontWeight: 800, color, margin: 0, lineHeight: 1 }}>{value ?? '—'}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEnterpriseAdmin = user?.role === 'enterprise_admin';
  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => {
    getStats().then(({ data }) => setStats(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const completionRate = stats
    ? Math.round((stats.completed_assessments / Math.max(stats.total_assessments, 1)) * 100)
    : 0;

  const dashTitle = stats?.enterprise_name ? `数据看板 · ${stats.enterprise_name}` : '数据看板';

  return (
    <AdminLayout title={dashTitle}>
      {loading ? (
        <p style={{ color: '#94a3b8' }}>加载中...</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
            <StatCard label={stats?.enterprise_name ? '本企业成员数' : '总用户数'} value={stats?.total_users} color="#2563eb" icon="👥" />
            {!stats?.enterprise_name && <StatCard label="企业数" value={stats?.total_enterprises} color="#7c3aed" icon="🏢" />}
            <StatCard label={stats?.enterprise_name ? '本企业测评数' : '总测评数'} value={stats?.total_assessments} color="#0891b2" icon="📋" />
            <StatCard label="已完成测评" value={stats?.completed_assessments} color="#16a34a" icon="✅" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* 完成率 */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: '#1e293b' }}>测评完成率</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ position: 'relative', width: 80, height: 80 }}>
                  <svg width="80" height="80" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="32" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                    <circle cx="40" cy="40" r="32" fill="none" stroke="#16a34a" strokeWidth="8"
                      strokeDasharray={`${completionRate * 2.01} 201`}
                      strokeDashoffset="50" strokeLinecap="round" />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#16a34a' }}>
                    {completionRate}%
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>完成 {stats?.completed_assessments} / 总计 {stats?.total_assessments}</p>
                </div>
              </div>
            </div>

            {/* 快捷操作 */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#1e293b' }}>快捷操作</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: '管理题库', path: '/admin/questions', color: '#2563eb', hide: isEnterpriseAdmin },
                  { label: '审核企业注册', path: '/admin/enterprises', color: '#7c3aed', hide: isEnterpriseAdmin },
                  { label: '配置 AI 策略', path: '/admin/ai-strategy', color: '#0891b2', hide: !isSuperAdmin },
                  { label: '成员管理', path: '/admin/users', color: '#0891b2', hide: !isEnterpriseAdmin },
                ].filter(item => !item.hide).map(item => (
                  <button key={item.path} onClick={() => navigate(item.path)} style={{
                    padding: '9px 16px', borderRadius: 8, border: `1.5px solid ${item.color}20`,
                    background: `${item.color}08`, color: item.color, fontWeight: 600, fontSize: 13,
                    cursor: 'pointer', textAlign: 'left',
                  }}>
                    {item.label} →
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
